import type { VFC } from 'react';
import { memo, useCallback, useRef } from 'react';
import isEqual from 'lodash/isEqual';

import { browserMetrics } from '@atlassian/browser-metrics';
import type { PageSegmentLoadMetric } from '@atlassian/browser-metrics/types';

import { VIEW_BLOG, VIEW_PAGE } from '@confluence/named-routes/entry-points/routes.ts';
import { getUniquePageLoadId } from '@confluence/unique-page-load-id/entry-points/UniquePageLoadId.ts';
import { SSRMeasures } from '@confluence/action-measures/entry-points/SSRMeasures.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withTransparentErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';

import type { ActionTiming } from './usePerformanceAction.tsx';
import { usePerformanceAction } from './usePerformanceAction.tsx';
import { PAGE_SEGMENT_MARK_PREFIX_END } from './metric-keys/index.ts';
import { browserMetricsState } from './utils/index.ts';
import { getViewTransitionSource } from './viewTransitionSource.ts';

export type Props = {
	/**
	 * The metric to stop.
	 */
	metric: PageSegmentLoadMetric;
	/**
	 * A callback that is fired right after the metric is started.
	 */
	onComplete?: () => void;
	/**
	 * A override to use for the metric stop time.
	 */
	stopTime?: number;
	/**
	 * The timing to use when starting the metric.
	 *
	 */
	timing?: ActionTiming;
	/**
	 * Custom data to add to the event's payload
	 */
	customData?: Record<string, any>;
	/**
	 * OnComplete might have sideeffects like measuring performance.
	 * This function gives an opportunity to enrich customData with more fields after the "onComplete" call
	 */
	augmentCustomDataAfterComplete?: (customData: Record<string, any>) => any;
};

const noop = () => {};

const PageSegmentLoadEndComponent: VFC<Props> = memo(
	({
		customData = {},
		metric,
		onComplete = noop,
		stopTime,
		timing,
		augmentCustomDataAfterComplete,
	}) => {
		// Use refs to avoid firing handlePageSegmentLoadMetricStop more than once despite changes to stopTime or customData
		const stopTimeRef = useRef(stopTime);
		const customDataRef = useRef(customData);
		const augmentCustomDataAfterCompleteRef = useRef(augmentCustomDataAfterComplete);

		stopTimeRef.current = stopTime;
		customDataRef.current = customData;
		augmentCustomDataAfterCompleteRef.current = augmentCustomDataAfterComplete;

		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if (process.env.REACT_SSR) {
			SSRMeasures.markMeasureEnd(`ssr-app/render/${metric.key}`);
		}

		const handlePageSegmentLoadMetricStop = useCallback(() => {
			if (metric) {
				onComplete();
				let finalCustomData;
				if (augmentCustomDataAfterCompleteRef.current) {
					finalCustomData = augmentCustomDataAfterCompleteRef.current(customDataRef.current);
				} else {
					finalCustomData = customDataRef.current;
				}

				const { route } = metric.getData();

				const shouldAddViewTransitionSource = route === VIEW_PAGE.name || route === VIEW_BLOG.name;
				if (shouldAddViewTransitionSource) {
					finalCustomData.viewTransitionSource = getViewTransitionSource();
				}

				browserMetrics.getPageLoadMetric().mark(`${PAGE_SEGMENT_MARK_PREFIX_END}:${metric.key}`);
				metric.stop({
					customData: {
						...finalCustomData,
						previousRoute: browserMetricsState.previousRoute,
						previousContentId: browserMetricsState.previousContentId,
						pageLoadInfo: getUniquePageLoadId(),
					},
					stopTime: stopTimeRef.current,
				});
			}
		}, [metric, onComplete]);

		usePerformanceAction(handlePageSegmentLoadMetricStop, timing);
		return null;
	},
	/**
	 * Use lodash isEqual as comparator to avoid unnecessary re-renders
	 * i.e. when an object is defined directly within customData prop
	 * `<PageLoadSegmentEnd ... customData={{ data: 'example' }} />`
	 * (Export only for testing)
	 */
	isEqual,
);

/**
 * @deprecated Use `SegmentLoadEnd` from `@confluence/browser-metrics/entry-points/SegmentLoadEnd`
 * instead. It automatically switches between the BM3 and UFO implementations based on the
 * `confluence_fe_deprecate_bm3` feature gate. Direct usage of `PageSegmentLoadEnd` will be
 * removed as part of the BM3 deprecation (CCP-7117).
 */
export const PageSegmentLoadEnd = withTransparentErrorBoundary({
	attribution: Attribution.BACKBONE,
})(PageSegmentLoadEndComponent);
